import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AuthLayout from '../../../components/Auth/AuthLayout';
import FormField from '../../../components/Auth/FormField';
import LoadingButton from '../../../components/Auth/LoadingButton';

const AdminPasswordExpiredPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 11) {
      newErrors.newPassword = 'Password must be at least 11 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.currentPassword && formData.newPassword && formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Simulate password update API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate success
      toast.success('Admin password updated successfully! You can now continue using the admin panel.');
      navigate('/admin');
    } catch (error) {
      setErrors({ submit: 'Failed to update admin password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[@$!%*?&]/.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthLabel = (strength) => {
    switch (strength) {
      case 0:
      case 1:
        return { label: 'Very Weak', color: 'bg-jet-red-500' };
      case 2:
        return { label: 'Weak', color: 'bg-red-400' };
      case 3:
        return { label: 'Fair', color: 'bg-yellow-400' };
      case 4:
        return { label: 'Good', color: 'bg-jet-green-400' };
      case 5:
        return { label: 'Strong', color: 'bg-jet-green-500' };
      default:
        return { label: '', color: '' };
    }
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);
  const strengthInfo = getPasswordStrengthLabel(passwordStrength);

  const warningIcon = (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
  );

  return (
    <AuthLayout
      title="Admin Password Expired"
      subtitle="Your admin password has expired for security reasons. Please update your password to continue accessing the admin panel."
      icon={warningIcon}
      iconBgColor="bg-jet-red-100"
      iconColor="text-jet-red-600"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Critical Security Notice */}
        <div className="bg-jet-red-50 border border-jet-red-200 rounded-md p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-jet-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
            <div>
              <h4 className="text-sm font-medium text-jet-red-900 mb-1">Critical Security Update Required</h4>
              <p className="text-sm text-jet-red-800">
                Your admin password has expired as part of our security policy. 
                You must update your password to continue accessing administrative functions.
              </p>
            </div>
          </div>
        </div>

        {/* Current Password */}
        <FormField
          label="Current Admin Password"
          name="currentPassword"
          value={formData.currentPassword}
          onChange={handleChange}
          placeholder="Enter current admin password"
          required
          error={errors.currentPassword}
          showPasswordToggle
          showPassword={showCurrentPassword}
          onTogglePassword={() => setShowCurrentPassword(!showCurrentPassword)}
        />

        {/* New Password */}
        <FormField
          label="New Admin Password"
          name="newPassword"
          value={formData.newPassword}
          onChange={handleChange}
          placeholder="Enter new admin password"
          required
          error={errors.newPassword}
          showPasswordToggle
          showPassword={showNewPassword}
          onTogglePassword={() => setShowNewPassword(!showNewPassword)}
        >
          {/* Password Strength Indicator */}
          {formData.newPassword && (
            <div className="mt-2">
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-jet-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${strengthInfo.color}`}
                    style={{ width: `${(passwordStrength / 5) * 100}%` }}
                  ></div>
                </div>
                <span className="text-xs text-jet-gray-600">{strengthInfo.label}</span>
              </div>
            </div>
          )}
        </FormField>

        {/* Confirm New Password */}
        <FormField
          label="Confirm New Admin Password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="Confirm new admin password"
          required
          error={errors.confirmPassword}
          showPasswordToggle
          showPassword={showConfirmPassword}
          onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
        />

        {/* Submit Error */}
        {errors.submit && (
          <div className="bg-jet-red-50 border border-jet-red-200 rounded-md p-3">
            <p className="text-sm text-jet-red-600">{errors.submit}</p>
          </div>
        )}

        {/* Enhanced Admin Password Requirements */}
        <div className="bg-jet-blue-50 border border-jet-blue-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-jet-blue-900 mb-2">Enhanced Admin Password Requirements:</h4>
          <ul className="text-sm text-jet-blue-800 space-y-1">
            <li className="flex items-center">
              <span className={`mr-2 ${formData.newPassword.length >= 8 ? 'text-green-600' : 'text-gray-400'}`}>
                {formData.newPassword.length >= 11 ? '✓' : '○'}
              </span>
              At least 11 characters long
            </li>
            <li className="flex items-center">
              <span className={`mr-2 ${/[a-z]/.test(formData.newPassword) ? 'text-green-600' : 'text-gray-400'}`}>
                {/[a-z]/.test(formData.newPassword) ? '✓' : '○'}
              </span>
              Contains lowercase letter
            </li>
            <li className="flex items-center">
              <span className={`mr-2 ${/[A-Z]/.test(formData.newPassword) ? 'text-green-600' : 'text-gray-400'}`}>
                {/[A-Z]/.test(formData.newPassword) ? '✓' : '○'}
              </span>
              Contains uppercase letter
            </li>
            <li className="flex items-center">
              <span className={`mr-2 ${/\d/.test(formData.newPassword) ? 'text-green-600' : 'text-gray-400'}`}>
                {/\d/.test(formData.newPassword) ? '✓' : '○'}
              </span>
              Contains number
            </li>
            <li className="flex items-center">
              <span className={`mr-2 ${/[@$!%*?&]/.test(formData.newPassword) ? 'text-green-600' : 'text-gray-400'}`}>
                {/[@$!%*?&]/.test(formData.newPassword) ? '✓' : '○'}
              </span>
              Contains special character (@$!%*?&)
            </li>
          </ul>
        </div>

        {/* Submit Button */}
        <LoadingButton
          type="submit"
          loading={loading}
          loadingText="Updating Admin Password..."
          variant="primary"
          className="bg-jet-red-600 hover:bg-jet-red-700 text-white"
        >
          Update Admin Password
        </LoadingButton>

        {/* Security Policy Information */}
        <div className="bg-jet-yellow-50 border border-jet-yellow-200 rounded-md p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-jet-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div>
              <h4 className="text-sm font-medium text-jet-yellow-900 mb-1">Admin Security Policy</h4>
              <p className="text-sm text-jet-yellow-800">
                Admin passwords expire every 60 days for enhanced security. 
                Choose a strong, unique password that you haven't used before. 
                All password changes are logged and monitored.
              </p>
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="text-center pt-4 border-t border-jet-gray-200">
          <p className="text-sm text-jet-gray-600 mb-2">
            Unable to update your password?
          </p>
          <button
            type="button"
            onClick={() => toast.info('Emergency Admin Support: admin-emergency@jetscanner360.com')}
            className="text-jet-red-600 hover:text-jet-red-700 text-sm font-medium transition-colors"
          >
            Contact Emergency Admin Support
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default AdminPasswordExpiredPage;